import {debounce} from 'lodash';
import React, {useEffect, useMemo, useState} from 'react';

import {
  Anchor,
  Button,
  Heading,
  Icon,
  IconButton,
  LoadingSpinner,
  SearchInput,
  SegmentedControl,
  TabNavigation,
  Text
} from '@albert-io/atomic';
import {SortDropdown} from 'client/Classwork/SortDropdown';
import type {ClassworkCreatedByFilter, SelectOption, SortValue} from 'client/Classwork/types';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import {history} from 'client/history';

import {AssignmentRowV2} from './AssignmentRowV2';
import type {StatusSegment} from './constants';
import {STATUS_SEGMENTS} from './constants';
import {FiltersMenu} from './FiltersMenu';
import type {AssignmentSortValue} from './hooks/useTeacherAssignments';
import {useTeacherAssignments} from './hooks/useTeacherAssignments';

const SORT_OPTIONS: ReadonlyArray<SelectOption<AssignmentSortValue>> = [
  {value: 'recentlyEdited', label: 'Recently Edited'},
  {value: 'name', label: 'Name'}
];

const DEFAULT_SORT: SortValue<AssignmentSortValue> = {field: 'recentlyEdited', order: 'desc'};

const getInitialSegment = (): StatusSegment => {
  const hash = window.location.hash.substring(1);
  // The legacy list linked here with #published/#drafts.
  if (hash === 'drafts') {
    return 'draft';
  }
  const match = STATUS_SEGMENTS.find((s) => s.value === hash);
  return match ? match.value : 'open';
};

interface Props {
  /** Scopes the list to one class (class detail page) and hides the classwork tabs. */
  classroomId?: string;
}

export const AssignmentsListV2 = ({classroomId}: Props) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);
  const [createdBy, setCreatedBy] = useState<ClassworkCreatedByFilter>('all');
  const [sort, setSort] = useState<SortValue<AssignmentSortValue>>(DEFAULT_SORT);
  const [segment, setSegment] = useState<StatusSegment>(getInitialSegment);

  const isDraftSegment = segment === 'draft';

  const actorTeacherId = masqueradeStore.getUserIdByMasqueradeState() ?? '';
  const {assignments, isLoading, hasNextPage, isLoadingMore, loadMore, refetch} =
    useTeacherAssignments({
      search: debouncedSearchTerm,
      segment,
      createdBy,
      actorTeacherId,
      sort,
      classroomId
    });

  const debouncedSetSearch = useMemo(() => debounce(setDebouncedSearchTerm, 200), []);
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const handleSegmentChange = (value: string | number) => {
    if (value === segment) {
      return;
    }
    window.location.hash = String(value);
    setSegment(value as StatusSegment);
    // Drafts are actor-only; clear createdBy so a carried-over filter doesn't empty the list.
    if (value === 'draft') {
      setCreatedBy('all');
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    debouncedSetSearch.cancel();
    setDebouncedSearchTerm('');
  };

  const resetFilters = () => {
    setCreatedBy('all');
    clearSearch();
  };

  // Keyed on the debounced term (what the query uses) so backspacing to empty doesn't flash
  // the onboarding zero-state while a stale filtered query is still in flight.
  const hasFiltersOn = createdBy !== 'all' || !!debouncedSearchTerm;
  const emptyNoun = isDraftSegment ? 'drafts' : `${segment} assignments`;

  return (
    <div className='flex flex-col'>
      {!classroomId && (
        <div className='-mx-x4 border-b border-primary px-x2'>
          <TabNavigation align='left' paddingLevel={0}>
            <TabNavigation.Tab isActive>
              <Icon className='mr-x1' icon='file-lines' iconStyle='regular' />
              Assignments
            </TabNavigation.Tab>
            <TabNavigation.Tab onClick={() => history.pushState(null, '/subjects')}>
              <Icon className='mr-x1' icon='books' iconStyle='regular' />
              Subjects
            </TabNavigation.Tab>
            <TabNavigation.Tab onClick={() => history.pushState(null, '/saved')}>
              <Icon className='mr-x1' icon='folder' iconStyle='regular' />
              Folders
            </TabNavigation.Tab>
          </TabNavigation>
        </div>
      )}
      <div className='flex flex-wrap items-center justify-between gap-x2 py-x4'>
        <SegmentedControl
          buttonClassName='font-normal'
          onSelect={handleSegmentChange}
          options={STATUS_SEGMENTS}
          selectedIdx={STATUS_SEGMENTS.findIndex((s) => s.value === segment)}
        />
        <div className='flex items-center gap-x1'>
          {/* Drafts have no applicable filters (no status/creator yet) — disabled, not hidden. */}
          <FiltersMenu createdBy={createdBy} disabled={isDraftSegment} onChange={setCreatedBy} />
          <SortDropdown
            options={SORT_OPTIONS}
            triggerIcon={['far', 'arrow-down-short-wide']}
            triggerLabel='Sort'
            value={sort}
            onChange={setSort}
          />
          {isSearchOpen ? (
            // React's onBlur bubbles like focusout: collapse when focus leaves with no query.
            <div
              onBlur={(e) => {
                if (!searchTerm && !e.currentTarget.contains(e.relatedTarget as Node | null)) {
                  setIsSearchOpen(false);
                }
              }}
            >
              <SearchInput
                autoFocus
                className='w-[256px]'
                placeholder='Search'
                rectangle
                value={searchTerm}
                onChange={(_e, value) => {
                  setSearchTerm(value);
                  debouncedSetSearch(value);
                }}
                onReset={() => {
                  clearSearch();
                  setIsSearchOpen(false);
                }}
              />
            </div>
          ) : (
            <IconButton
              color='primary'
              icon='magnifying-glass'
              label='Search assignments'
              roundness='square'
              variant='outlined'
              onClick={() => setIsSearchOpen(true)}
            />
          )}
        </div>
      </div>
      {isLoading ? (
        <LoadingSpinner size={3} icon='loader' iconStyle='light' className='p-x6' />
      ) : (
        <>
          {!assignments.length && !hasFiltersOn && (
            <div className='flex flex-col items-center gap-x1 py-x6 text-center'>
              <Heading as='h2' size='xs'>
                You have no {emptyNoun}
              </Heading>
              <Text color='secondary' size='s'>
                Your {emptyNoun} will appear on this page
              </Text>
              <Button
                className='mt-x2'
                onClick={() => history.pushState(null, '/assignment-builder/create-assignment')}
              >
                New Assignment
              </Button>
            </div>
          )}
          {!assignments.length && hasFiltersOn && (
            <div className='flex flex-col items-center gap-x1 py-x6 text-center'>
              <Heading as='h2' size='xs'>
                No {isDraftSegment ? 'drafts' : 'assignments'} found
              </Heading>
              <Anchor underlined onClick={resetFilters}>
                Clear filters
              </Anchor>
            </div>
          )}
          {!!assignments.length && (
            <ul className='m-x0 flex list-none flex-col p-x0'>
              {assignments.map((assignment) => (
                <li key={assignment.id}>
                  <AssignmentRowV2
                    actorTeacherId={actorTeacherId}
                    assignment={assignment}
                    classroomId={classroomId}
                    status={segment}
                    onDeleteComplete={refetch}
                  />
                </li>
              ))}
            </ul>
          )}
          {hasNextPage && (
            <div className='flex justify-center py-x3'>
              <Button disabled={isLoadingMore} onClick={loadMore}>
                Show more
                {isLoadingMore && <Icon className='ml-x1' icon='spinner' spin />}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
